import { ErrorState, LoadingState } from '~/components/shared/states';
import { useServiceTypes } from '~/lib/queries';
import { cn } from '~/lib/utils';
import type { BookingDraft } from '../booking-types';
import { StepShell } from '../step-shell';

/**
 * Tunnel de reservation — parcours principal de l'application.
 *
 * L'ordre des etapes suit litteralement l'enonce : adresse, cycle, operation de
 * maintenance, forfait, creneau, complements. Deux partis pris :
 *
 *   - La couverture est verifiee DES l'etape 1. Un client hors zone l'apprend
 *     immediatement, avant d'avoir saisi quoi que ce soit d'autre — plutot qu'apres
 *     cinq etapes, ce qui serait une perte de temps et une mauvaise experience.
 *
 *   - Le compte n'est jamais un prealable (RG-04). Un visiteur connecte se voit
 *     simplement proposer ses adresses et ses cycles enregistres.
 */

export function OperationStep({
  draft,
  patch,
  onBack,
  onNext,
}: {
  draft: BookingDraft;
  patch: (values: Partial<BookingDraft>) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const { data: serviceTypes, isLoading, error, refetch } = useServiceTypes();

  return (
    <StepShell
      title="De quoi avez-vous besoin ?"
      description="Choisissez le type de prestation ; le forfait precis vient a l'etape suivante."
      onBack={onBack}
      onNext={onNext}
      nextDisabled={!draft.serviceTypeId}
    >
      {isLoading ? <LoadingState rows={3} /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      <div className="grid gap-3">
        {serviceTypes?.map((service) => (
          <button
            key={service.id}
            type="button"
            onClick={() =>
              patch({ serviceTypeId: service.id, packageId: undefined, slotIds: undefined })
            }
            className={cn(
              'rounded-lg border p-4 text-left transition-colors hover:bg-accent',
              draft.serviceTypeId === service.id ? 'border-primary bg-accent' : 'border-border',
            )}
            aria-pressed={draft.serviceTypeId === service.id}
          >
            <p className="font-medium">{service.name}</p>
            <p className="mt-1 text-sm text-muted-foreground">{service.description}</p>
          </button>
        ))}
      </div>
    </StepShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Etape 4 — Forfait (RG-06)
// ═══════════════════════════════════════════════════════════════════════════════
